/**
 * @fileoverview CardNameRow: a card's name as the Edit button (B158). A 44px row with the name in the title face and a small
 * muted pencil at its end; the whole row is the button. It is the plant card's name row, taken out so the bed card draws the
 * same one: the row owns its `-mt-2 -mb-1` and `data-card-head` (the attribute the card's pop-up looks for). An optional mark
 * can sit before the name (the Seed vault's packet) and after it (the Example pill). Without `onEdit` (an archived card) it is
 * the same row with no button and no pencil.
 *
 * It draws what it is given and decides nothing.
 *
 * Where: the plant card (PlantCard) and the bed card (LocationCard).
 * API: none.
 * Words: none; the caller gives the name and the button's aria-label.
 *
 * @module CardNameRow
 */

import * as React from 'react';
import { Pencil } from 'lucide-react';
import { CardTitle } from '@/shared/components/ui/card';
import { NameParts } from '@/shared/components/NameParts';
import type { NamePart } from '@/shared/types/plant';

/** What the row is given. */
export interface CardNameRowProps {
  /** The name as text. Give this or `parts`. */
  name?: React.ReactNode;
  /** The name as ready pieces (NameParts), when the server sends it so. Wins over `name`. */
  parts?: ReadonlyArray<NamePart>;
  /** A mark drawn before the name (the vault's packet). */
  before?: React.ReactNode;
  /** A mark drawn after the name, before the pencil (the Example pill). */
  after?: React.ReactNode;
  /** Opens Edit. Without it the row is plain: no button, no pencil. */
  onEdit?: () => void;
  /** The button's aria-label (only used with `onEdit`). */
  ariaLabel?: string;
}

/**
 * The name row.
 *
 * @param props - CardNameRowProps.
 */
export const CardNameRow: React.FC<CardNameRowProps> = ({ name, parts, before, after, onEdit, ariaLabel }) => {
  /** The name, drawn the same way in both forms of the row. */
  const nameEl = <span className="min-w-0">{parts ? <NameParts parts={parts} /> : name}</span>;
  return (
    <div data-card-head className="-mb-1 -mt-2">
      <CardTitle className="text-xl">
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={ariaLabel}
            className="flex min-h-11 w-full min-w-0 items-center gap-2 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {before}
            {nameEl}
            {after}
            <Pencil className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </button>
        ) : (
          <div className="flex min-h-11 min-w-0 items-center gap-2">
            {before}
            {nameEl}
            {after}
          </div>
        )}
      </CardTitle>
    </div>
  );
};
